import { Component, OnInit, Inject, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { AccountType } from '@mnz-core/models/_enums';
import { IRootState } from '@mnz-core/modules/store/models/root-state';
import * as routerSelectors from '@mnz-core/modules/store/selectors/router.selectors';
import { createLoginForm } from '@mnz-modules/login/login.constants';
import * as authActions from '@mnz-modules/login/store/actions/auth.actions';
import * as authSelectors from '@mnz-modules/login/store/selectors/auth.selectors';
import { formUtils } from '@mnz-shared/shared.constants';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { APP_CONFIG } from '@mnz-core/injectors/app-configuration';
import { IAppConfiguration } from '@mnz-core/models/app-configuration';
import { ICredentials } from '@mnz-modules/login/models/credentials';
import { Platform } from '@mnz-core/models/_enums';
import { MNZ_PLATFORM } from '@mnz-core/injectors/platform';
declare let grecaptcha: any;

@Component({
  selector: 'mnz-login-form',
  templateUrl: 'login-form.component.html',
  styleUrls: ['login-form.component.scss'],
})
export class LoginFormComponent implements OnInit {
  @ViewChild('credentialInput') credentialInput: ElementRef;
  @ViewChild('passwordInput') passwordInput: ElementRef;

  subscriptions = new Subscription();
  captchaKey: string;
  loginCredentials: ICredentials;
  credentialValue: string;
  credentialErrorMessage$: Observable<string>;
  passwordErrorMessage$: Observable<string>;
  passwordInputId = 'password';
  showPassword = false;

  private readonly _captcha = new Subject<string>();

  readonly captcha$ = this._captcha.asObservable();

  constructor(
    @Inject(APP_CONFIG) private readonly _config: IAppConfiguration,
    @Inject(MNZ_PLATFORM) private readonly platform: Platform,
    private readonly _store$: Store<IRootState>,
    private readonly _translate: TranslateService,
    private readonly _formBuilder: FormBuilder,
    private readonly _router: Router
  ) {
    this.loadScript();

    this._store$.dispatch(authActions.resetState());

    combineLatest([
      this._store$.pipe(
        select(routerSelectors.getQueryParams),
        map<Params, string>(({ email }) => email)
      ),
      this.loginForm$,
    ])
      .pipe(
        filter(([email]) => email != null),
        first()
      )
      .subscribe(([email, form]) => form.get('credential').setValue(email));
  }

  ngOnInit(): void {
    window['getResponceCapcha'] = this.getResponceCapcha.bind(this);
  }

  public togglePassWord(): void {
    this.showPassword = !this.showPassword;
  }

  readonly accountType$ = this._store$.pipe(
    select(routerSelectors.getParams),
    map<Params, AccountType>(({ account }) => account)
  );

  getResponceCapcha(captchaResponse: string) {
    this._captcha.next(captchaResponse);
  }

  readonly loading$ = this._store$.pipe(select(authSelectors.getLoading));

  readonly loginForm$ = createLoginForm(this._formBuilder, this.accountType$);

  readonly nameAttr$ = this.accountType$.pipe(
    map((account) => (account === AccountType.CARD_HOLDER ? 'email' : 'username'))
  );
  readonly typeAttr$ = this.accountType$.pipe(
    map((account) => (account === AccountType.CARD_HOLDER ? 'email' : 'text'))
  );

  readonly placeHolderTranslation$ = this.accountType$.pipe(
    switchMap((account) => {
      return this._translate.get(account === AccountType.CARD_HOLDER ? 'EMAIL' : 'USERNAME');
    })
  );

  onCloseMessage(): void {
    this._store$.dispatch(authActions.resetFailMessage());
  }

  readonly loginErrorTranslation$ = this._store$.pipe(
    select(authSelectors.getMessageFail),
    switchMap((message) => {
      return (message ? this._translate.get(message.body) : of(null))})
      );

  readonly usernameErrorTranslation$ = this.accountType$.pipe(
    switchMap((account) => {
      return this._translate.get(
        `LOGIN.INPUT_ERROR.${account === AccountType.CARD_HOLDER ? 'INVALID_EMAIL' : 'INVALID_USERNAME'}`
      );
    })
  );

  readonly usernameRequiredTranslation$ = this.accountType$.pipe(
    switchMap((account) => {
      return this._translate.get(
        `LOGIN.INPUT_ERROR.${account === AccountType.CARD_HOLDER ? 'REQUIRED_EMAIL' : 'REQUIRED_USERNAME'}`
      );
    })
  );

  readonly usernameLengthTranslation$ = this.accountType$.pipe(
    switchMap((account) => {
      return this._translate.get(
        `LOGIN.INPUT_ERROR.${account === AccountType.CARD_HOLDER ? 'LENGTH_EMAIL' : 'LENGTH_USERNAME'}`
      );
    })
  );

  onInputBlur(control: FormControl): void {
    return control.value && formUtils.touchControls({ control });
  }

  public onKeyDown(form) {
    this.login(form);
  }

  async login(form: FormGroup): Promise<void> {
    const { recaptchaEnabled } = this._config;

    /**
     * Workarround for password managers
     * Password managers will inject values into input fields which
     * do not trigger a change detection
     */
    form.get('credential').setValue(this.credentialInput.nativeElement.value.trim(), { emitEvent: false });
    form.get('password').setValue(this.passwordInput.nativeElement.value, { emitEvent: false });

    if (!form.valid) {
      if (form.get('credential').hasError('required') || form.get('credential').hasError('credential')) {
        this.checkCredentials(form);
        this.credentialInput.nativeElement.focus();
      } else if (form.get('password').hasError('required')) {
        this.checkPassword(form);
        this.passwordInput.nativeElement.focus();
      }
      return formUtils.touchControls(form.controls);
    }

    if (recaptchaEnabled) {
      grecaptcha.reset();
      grecaptcha.execute();
    }

    const accountType = await this.accountType$.pipe(first()).toPromise();
    this.loginCredentials = {
      username: form.controls.credential.value.trim(),
      password: form.controls.password.value,
    };

    const credentials = {
      username: this.loginCredentials.username,
      password: this.loginCredentials.password,
    };

    const captcha = recaptchaEnabled ? await this.captcha$.pipe(first()).toPromise() : null;
    this._store$.dispatch(authActions.authenticate({ credentials, accountType, captcha }));
  }

  requestAccount(): void {
    this._router.navigate(['/account-activeren']);
  }

  async lostCredentials(form: FormGroup): Promise<void> {
    const accountType = await this.accountType$.pipe(first()).toPromise();
    const email = form.get('credential').value;

    let userInput: string;
    if (accountType === AccountType.CARD_HOLDER && email) {
      userInput = email;
    } else if (accountType === AccountType.CONTACT_PERSON) {
      userInput = '';
    }
    this._store$.dispatch(authActions.setUserInput({ userInput }));
    this._router.navigate([`/inlog-vergeten/${accountType}`]);
  }

  public loadScript() {
    const url = 'https://www.google.com/recaptcha/api.js';
    const node = document.createElement('script');
    node.src = url;
    node.async = true;
    node.defer = true;
    const head = document.getElementsByTagName('head')[0];
    const { recaptchaEnabled } = this._config;
    if (recaptchaEnabled) {
      head.appendChild(node);
    }
  }

  public checkCredentials(form) {
    const credential = form.controls.credential;
    const isTouchedDirtyAndInvalid = this.isTouchedDirtyAndInvalid(credential);

    if (isTouchedDirtyAndInvalid) {
      if (credential.hasError('pattern') || (credential.hasError('credential') && !credential.hasError('required'))) {
        this.credentialErrorMessage$ = this.usernameErrorTranslation$;
      } else if (credential.hasError('required')) {
        this.credentialErrorMessage$ = this.usernameRequiredTranslation$;
      } else if (credential.hasError('maxlength') && !credential.hasError('pattern')) {
        this.credentialErrorMessage$ = this.usernameLengthTranslation$;
      }
    } else {
      this.credentialErrorMessage$ = of('');
    }
  }

  public checkPassword(form) {
    const password = form.controls.password;
    const isTouchedDirtyAndInvalid = this.isTouchedDirtyAndInvalid(password);

    if (isTouchedDirtyAndInvalid && password.hasError('required')) {
      this.passwordErrorMessage$ = this._translate.get('LOGIN.INPUT_ERROR.REQUIRED_PASSWORD');
    } else {
      this.passwordErrorMessage$ = of('');
    }
  }

  private isTouchedDirtyAndInvalid(control) {
    return !control.valid && control.touched && control.dirty;
  }

  ngOnDestory() {
    this.subscriptions.unsubscribe();
  }
}
